import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, provideHttpClient } from '@angular/common/http';
import { EnvironmentProviders, ErrorHandler, InjectionToken, importProvidersFrom, inject } from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { IonicRouteStrategy, Platform, ToastController, provideIonicAngular } from '@ionic/angular';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { routes } from 'src/app/app.routes';
import { graphqlProvider } from 'src/app/graphql.provider';
import { AuthInterceptor } from 'src/app/interceptors/auth.interceptor';
import { LoadingInterceptor } from 'src/app/interceptors/loading.interceptor';
import { NotAuthInterceptor } from 'src/app/interceptors/not-auth.interceptor';
import { AppComponent } from './app/app.component';
import { applicationLanguages, languageTranslationFilesDictionary } from './app/models/dictionaries/application-languages';
import { ApplicationInsightsService } from './app/services/error-handling/application-insights.service';
import { errorHandlerFactory } from './app/services/error-handling/error-handler-factory';

import { provideAnimations } from '@angular/platform-browser/animations';
import { forkJoin, map } from 'rxjs';
defineCustomElements(window);

export const PLATFORM_TOKEN = new InjectionToken<Platform>('Platform');

// Define the token
bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(BrowserModule),
        provideHttpClient(),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
            fallbackLang: applicationLanguages[0].value,
        }),
        provideAnimations(),
        importProvidersFrom(
            NgCircleProgressModule.forRoot({
                radius: 100,
                space: -10,
                maxPercent: 100,
                outerStrokeWidth: 10,
                outerStrokeColor: '#6cca98',
                outerStrokeGradientStopColor: '#53a9ff',
                innerStrokeColor: '#e7e8ea',
                innerStrokeWidth: 10,
                titleFontSize: '36',
                titleFontWeight: '400',
                unitsFontSize: '36',
                unitsFontWeight: '400',
                animateTitle: false,
                animationDuration: 0,
                showSubtitle: false,
                showUnits: false,
                showBackground: false,
                responsive: true,
                startFromZero: false,
            })
        ),
        importProvidersFrom(NgIdleKeepaliveModule.forRoot()),
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        provideIonicAngular({
            mode: 'md',
        }),
        provideRouter(routes, withComponentInputBinding()),
        { provide: provideServiceWorker, useFactory: serviceWorkerFactory },
        graphqlProvider,
        importProvidersFrom(HttpClientModule), // needed for HTTP_INTERCEPTORS to work
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NotAuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoadingInterceptor,
            multi: true,
        },
        ApplicationInsightsService,
        { provide: ErrorHandler, useFactory: errorHandlerFactory, deps: [ToastController, ApplicationInsightsService] },
    ],
}).catch((err) => console.error(err));

export function serviceWorkerFactory(): EnvironmentProviders {
    const platform = inject(Platform);
    return provideServiceWorker('ngsw-worker.js', { enabled: platform.is('pwa'), registrationStrategy: 'registerWhenStable:30000' });
}

export function HttpLoaderFactory(http: HttpClient) {
    // Load translations for multiple languages
    return new MultiTranslateHttpLoader(http, languageTranslationFilesDictionary);
}

// // Custom Translation Loader
export class MultiTranslateHttpLoader implements TranslateLoader {
    constructor(
        private http: HttpClient,
        private resources: { [lang: string]: string[] }
    ) {}

    getTranslation(lang: string) {
        const requests = this.resources[lang].map((url) => this.http.get(url));
        return forkJoin(requests).pipe(map((results) => Object.assign({}, ...results)));
    }
}
